<template>
  <div class="resources-hero">
    <div class="hero-top">
      <div class="head-layout container col-6">
        <h1 class="h2">
          {{ title }}
        </h1>
        <span class="subtitle">
          {{ categorySubtitle }}
        </span>
        <form :class="{ 'search-open': results && results.length }" @submit.prevent>
          <input
            v-model="query"
            type="text"
            placeholder="Search"
            autocomplete="off"
            tabindex="0"
            data-event="search.click"
            data-direct-send
            @keyup.enter.exact.prevent="redirectToSearchPage"
            @keyup.arrow-down.exact.prevent="arrowsPress"
            @keyup.arrow-up.exact.prevent="arrowsPress"
            @keyup="search"
            @focus="search"
          >
          <button type="button">
            <CloseSearch v-if="query" class="close-search" @click="clearSearch(true)" />
            <Search v-else class="search" />
          </button>

          <ul v-show="query && results && results.length && show" class="search-results">
            <li v-for="(result, idx) in results" :key="idx">
              <nuxt-link
                :to="{ path: `/resources-center/${result.categorySlug}/${result.slug}/` }"
                :class="{ focused: idx === focus }"
              >
                {{ result.name }}
              </nuxt-link>
            </li>
          </ul>
        </form>
      </div>
    </div>
    <div v-if="!$route.query.q" class="hero-bottom categories-wrapper">
      <ul class="categories container scrollable">
        <li
          v-for="(cat, i) in filters"
          :key="i"
          :class="[cat.slug, { active: cat.slug === category }]"
          @click="selectCategory(cat.slug)"
        >
          <nuxt-link :to="cat.slug === '' ? '/resources-center/' : `/resources-center/${cat.slug}/`">
            <div class="icon">
              <Component :is="cat.icon" />
            </div>
            <p class="name blog-post-title-6">
              {{ cat.name }}
            </p>
          </nuxt-link>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import { debounce } from 'lodash-es';
import Search from '../../assets/svg/icon-search.svg?vue-component';
import CloseSearch from '../../assets/svg/icon-close.svg?vue-component';
import Notebook from '../../assets/svg/icon-resources-notebook.svg?vue-component';
import Bookmark from '../../assets/svg/icon-resources-bookmark.svg?vue-component';
import Notepad from '../../assets/svg/icon-resources-notepad.svg?vue-component';
import ChartPie from '../../assets/svg/icon-resources-chartpie.svg?vue-component';
import Chat from '../../assets/svg/icon-resources-chats.svg?vue-component';
import ChartLine from '../../assets/svg/icon-resources-chartline.svg?vue-component';
import AddressBook from '../../assets/svg/icon-resources-addressbook.svg?vue-component';

export default {
  components: {
    Search, CloseSearch, Notebook, Bookmark, Notepad, ChartPie, Chat, ChartLine, AddressBook,
  },
  props: {
    count: { type: Number, default: 0 },
  },
  data() {
    return {
      category: this.$route.params.category || '',
      query: this.$route.query.q || '',
      results: [],
      focus: -1,
      validQuery: true,
      show: false,
    };
  },
  computed: {
    filters() {
      const filters = [
        { icon: 'Notebook', name: 'All', slug: '' },
        { icon: 'Bookmark', name: 'Course Notes', slug: 'course-notes' },
        { icon: 'Notepad', name: 'Templates', slug: 'templates' },
        { icon: 'ChartPie', name: 'Infographics', slug: 'infographics' },
        // { icon: 'Chat', name: 'Interview questions', slug: 'interview-questions' },
        { icon: 'ChartLine', name: 'Career Guides', slug: 'career-guides' },
        { icon: 'AddressBook', name: 'Practice Exams', slug: 'practice-exams' },
      ];

      return filters;
    },
    title() {
      if (this.$route.query.q) {
        return `Search Results for: ${this.$route.query.q}`;
      }

      if (this.category) {
        const currentCategory = this.filters.find((v) => this.category === v.slug) || {};
        let preTitle = 'Data Science';
        if (currentCategory.slug === 'career-guides') {
          preTitle = 'Data and AI';
        } else if (currentCategory.slug === 'infographics') {
          preTitle = 'Data Science and AI';
        }

        if (currentCategory.name) {
          return `${preTitle} ${currentCategory.name}`;
        }
      }

      return '365 Data Science Resources Center';
    },
    categorySubtitle() {
      if (this.category === 'course-notes') {
        return 'Enhance your learning journey with in-depth data science lecture notes. Deepen your understanding of core concepts in data science, statistics, probability, Python, and machine learning.';
      }
      if (this.category === 'templates') {
        return 'Boost your programming skills with free SQL, R, Excel, and Python templates and create top-notch projects for your portfolio.';
      }
      if (this.category === 'infographics') {
        return 'We break down complex topics in data science, data analytics, machine learning, and AI into informative, easy-to-digest infographics.';
      }
      if (this.category === 'career-guides') {
        return 'At 365 Data Science, we support you in pursuing your goals and finding a career that fits your needs. Understand the plethora of data and AI jobs and what it takes to succeed in today’s job market.';
      }
      if (this.category === 'practice-exams') {
        return 'How well do you know the most in-demand data science and analytics tools and techniques? Test your data science skills with practice problems and exam questions.';
      }
      return `Discover over ${this.count} free data science resources to simplify your learning journey. Enhance your data and AI training with comprehensive study materials, career guidance, and expert advice.`;
    },
  },
  watch: {
    '$route.query.q': function query(v) {
      this.query = v;
    },
  },
  mounted() {
    this.$root.$on('emit-click', (e) => {
      if (this.show && !e.target.closest('.search-open')) {
        this.show = false;
      }
    });
  },
  methods: {
    selectCategory(slug) {
      this.category = slug;

      const categories = document.querySelector('.categories-wrapper');
      const y = categories.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top: y, behavior: 'smooth' });
    },
    redirectToSearchPage() {
      const { query } = this;

      if (!query) {
        this.validQuery = false;
        return false;
      }

      this.validQuery = true;

      if (this.focus >= 0) {
        // when click enter and item of dropdown is focused
        const { categorySlug, slug } = this.results[this.focus];

        return this.$router.push(`/resources-center/${categorySlug}/${slug}/`);
      }

      this.clearSearch();

      let category;
      let technology;

      if (this.$route.name === 'resources-center-category') {
        if (this.$route.params.category) {
          category = this.$route.params.category;
        }

        if (this.$route.query.technology) {
          technology = this.$route.query.technology;
        }
      }

      return this.$router.push({ path: '/resources-center/search/', query: { q: query, category, technology } });
    },
    search: debounce(async function delay(e) {
      if (!e) {
        return;
      }

      if (e.code === 'Enter' || e.code === 'ArrowDown' || e.code === 'ArrowUp') {
        return;
      }

      if (this.query.length < 1) {
        return;
      }

      this.focus = -1;
      const resources = await this.$axios.$get(`/resources/search/suggestions?filter[q]=${this.query}&mobile=${+(this.$store.state.layout === 'mobile')}`, { progress: false });
      this.results = resources;
      this.show = true;
    }, 500),
    clearSearch(force = false) {
      if (this.$route.name === 'resources-center-search' && force) {
        setTimeout(() => {
          this.$router.push('/resources-center/');
        }, 100);
      }

      setTimeout(() => {
        this.query = '';
        this.focus = -1;
        this.results = [];
        this.show = false;
      }, 100);
    },
    arrowsPress(e) {
      if (e.code === 'ArrowDown') {
        if (this.focus >= this.results.length - 1) {
          this.focus = 0;
        } else {
          this.focus += 1;
        }
        return;
      }

      if (this.focus === 0) {
        this.focus = this.results.length - 1;
      } else {
        this.focus -= 1;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.hero-top {
  position: relative;
  display: flex;
  align-content: center;
  align-items: center;
  height: 505px;
  color: var(--main-light);
  background: linear-gradient(180deg, var(--main-dark) 0%, rgba(0, 12, 31, 0) 100%);
  background-color: var(--main-dark);
  text-align: center;

  .subtitle {
    color: var(--main-dark-shade-4);
  }

  form {
    max-width: 485px;
    margin: 0 auto;
    margin-top: var(--spacing-36);
    position: relative;
    border-radius: 5px;
    z-index: 1;

    input {
      &::placeholder {
        color: var(--main-light-shade-4);
        font-size: var(--fs-base);
      }

      &:not(:focus):hover {
        border: 1px solid var(--main-light-shade-4);
      }

      & + .alert {
        font-size: 14px;
        text-align: left;
        margin-top: 5px;
        padding-left: var(--spacing-48);
        color: var(--main-red);
      }
    }

    button {
      position: absolute;
      height: 52px;
      top: 2px;
      right: 0;
      padding-right: var(--spacing-26);
      background-color: transparent;
      border: none;
      cursor: pointer;

      .close-search {
        width: 18px;
        height: 18px;

        path[fill] {
          fill: var(--main-light-shade-4);
        }

        path[stroke] {
          stroke: var(--main-light-shade-4);
        }
      }

      .search path {
        stroke: var(--main-dark-light-3);
      }
    }

    .search-results {
      position: absolute;
      left: 0;
      top: 90%;
      background: var(--main-light);
      border-bottom-left-radius: 10px;
      border-bottom-right-radius: 10px;
      width: 100%;
      text-align: left;
      padding: 0;
      list-style: none;
      box-shadow: 0 16px 20px rgba(0, 0, 0, 0.15);
      z-index: 1;
      overflow: hidden;
      margin: 0;
      border: 1px solid var(--main-dark-opacity-18);
      border-top: none;

      a {
        padding-left: 14px;
        padding-top: 10px;
        padding-bottom: 10px;
        color: var(--true-black);
        font-size: 14px;
        font-weight: 500;
        display: block;
        line-height: 24px;

        &:hover,
        &:focus,
        &.focused {
          background-color: var(--accent-light-1);
          text-decoration: none;
        }

        &:active {
          color: var(--accent);
        }
      }
    }

    &.search-open {
      input {
        border-bottom-right-radius: 0;
        border-bottom-left-radius: 0;

        &:focus,
        &:hover {
          border-color: var(--main-dark-opacity-18);
        }
      }
    }
  }
}

.categories-wrapper {
  background: var(--main-dark);

  .categories {
    list-style: none;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-direction: row;
    color: var(--main-light);
    padding: 0;
    margin: 0 auto;

    li {
      cursor: pointer;
      position: relative;

      a {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding-bottom: 60px;

        .icon {
          background: var(--main-dark-shade-1);
          border-radius: 80px;
          width: 60px;
          height: 60px;
          display: flex;
          align-items: center;
          justify-content: center;

          svg {
            opacity: 0.67;
          }
        }

        .name {
          margin-bottom: 0;
          color: var(--main-light-shade-4);
          font-weight: 500;
        }
      }

      &:hover,
      &.active {
        .icon {
          border: 2px solid var(--accent);

          * {
            opacity: 1;
          }
        }

        .name {
          color: var(--accent);
        }

        &:before {
          content: "";
          position: absolute;
          bottom: 0;
          left: 50%;
          transform: translateX(-50%);
          border-left: 15px solid transparent;
          border-right: 15px solid transparent;
          border-bottom: 18px solid var(--main-dark-shade-1);
        }
      }

      &:hover:not(.active) {
        &:before {
          content: none;
        }
      }
    }
  }
}

@include bp($bp-mobile) {
  .hero-top {
    height: 416px;

    .h2 {
      font-size: 32px;
    }
  }

  .categories-wrapper .categories {
    margin: -1px auto;
    padding: 0 var(--spacing-24);

    li {
      padding-bottom: var(--spacing-28);
      margin: 0 var(--spacing-12);

      &:first-child {
        margin-left: 0;
      }

      a {
        .icon {
          height: 45px;
          width: 45px;

          svg {
            transform: scale(0.75);
          }
        }

        .name {
          font-size: 14px;
        }
      }
    }

    &.scrollable {
      width: 100%;
      overflow-x: auto;
      white-space: nowrap;
      position: relative;

      &::-webkit-scrollbar {
        display: none;
      }
    }
  }
}
</style>
